/**
 * What the prescription viewer accepts from the mouse, wheel and pointer.
 *
 * `panZoom.ts` is the geometry; this is the gate in front of it. Browsers and
 * operating systems disagree about what a wheel notch or a pointer event
 * carries, and the viewer was written and tested on Chrome + Mac, where the
 * disagreements do not show. A pharmacist on Chrome + Windows saw them (#396).
 * Each rule below names the input it exists for.
 *
 * Pure: event fields in, numbers and booleans out, so the rules are testable
 * without a DOM.
 */

/** `WheelEvent.deltaMode`, spelled once. The DOM's constants, not ours. */
export const WheelDeltaMode = {
  pixel: 0,
  line: 1,
  page: 2,
} as const

/*
 * One line of wheel scroll, in pixels.
 *
 * Firefox, and Windows set to "scroll N lines", can report in lines instead of
 * pixels. Three lines is one notch, and a notch is 100px in Chrome -- so a line
 * is a third of that, and one notch zooms by the same amount whatever the
 * browser counted it in.
 */
export const WHEEL_LINE_PX = 100 / 3

/*
 * The most one wheel event may move the zoom, in pixel-equivalents.
 *
 * One Chrome notch is 100. A Windows mouse set to scroll more lines per notch,
 * a display at 125% or 150% scaling, a free-spinning wheel or a page-mode
 * wheel all send several times that in a single event, and at the viewer's
 * sensitivity a 500px event is a 2x jump -- the prescription leaps past the
 * line being read instead of moving towards it. Capping each event at one
 * notch keeps a notch a notch everywhere; a fast spin still zooms fast,
 * because it sends more events, not bigger ones.
 */
export const MAX_WHEEL_STEP_PX = 100

/**
 * A wheel event's vertical delta as pixels, bounded to one notch.
 *
 * `pagePx` is what a "page" means here -- the field's height -- used only for
 * the rare page-mode wheel. A non-finite delta, which some synthetic and
 * driver-generated events carry, zooms nothing rather than poisoning the
 * transform with NaN.
 */
export function normalizedWheelDelta(deltaY: number, deltaMode: number, pagePx: number): number {
  if (!Number.isFinite(deltaY)) return 0
  const px =
    deltaMode === WheelDeltaMode.line
      ? deltaY * WHEEL_LINE_PX
      : deltaMode === WheelDeltaMode.page
        ? deltaY * Math.max(pagePx, 0)
        : deltaY
  return Math.max(-MAX_WHEEL_STEP_PX, Math.min(MAX_WHEEL_STEP_PX, px))
}

/** `PointerEvent.button`: which button changed state on a press. */
export const PointerButton = {
  primary: 0,
  middle: 1,
  secondary: 2,
} as const

/**
 * Whether a press starts a pan.
 *
 * The primary button, and the middle one. The right button is the context
 * menu's: on Windows that menu opens on *release*, after our pointer capture,
 * so a right-press that started a pan could lose its release to the menu and
 * leave the prescription stuck to the cursor. The middle button pans because
 * on Windows it otherwise starts Chrome's autoscroll -- the page slides away
 * under a round cursor -- which is the one thing a press on the prescription
 * must not do. Touch and pen report the primary button.
 */
export function startsPan(button: number): boolean {
  return button === PointerButton.primary || button === PointerButton.middle
}

/**
 * Whether a drag the viewer thinks is live has, in fact, already ended.
 *
 * A move with no button held means the release went somewhere else: an
 * Alt+Tab mid-drag, a release over another window, a pointer capture the
 * browser took back. Without this the pan kept following the cursor after
 * the button was up, and the prescription wandered off as the mouse moved
 * towards the other pane.
 */
export function releasedElsewhere(buttons: number): boolean {
  return buttons === 0
}

/**
 * Whether a pointer event carries a usable position.
 *
 * V1 froze a tab on this: an event with no coordinate made the delta NaN, the
 * NaN went into the transform, and the image vanished with no error at all.
 */
export function hasPosition(clientX: number, clientY: number): boolean {
  return Number.isFinite(clientX) && Number.isFinite(clientY)
}
